import type { Messages } from '../de';

export default {
  title: 'Settings',
  kickerDesktop: 'Everything this instance needs – none of it lives in the code',
  kickerMobile: 'Applies to this instance',
  back: 'More',
  loadError: 'Settings can’t be loaded right now.',
  retry: 'Try again',
  loading: 'Loading …',
  area: {
    quellen: 'Data sources', anmeldung: 'Sign-in', sportarten: 'Sports', zonen: 'Zones', sprache: 'Language & units',
    darstellung: 'Appearance', ki: 'AI & privacy', daten: 'Data', wetter: 'Weather',
  },
  areaSub: {
    wetter: 'Weather on activities with GPS, from Open-Meteo',
    anmeldung: 'Who may open the app. Chosen during setup via the server environment, only shown here.',
    sportarten: 'Per sport: name, pace display and which zones drive the load',
    zonen: 'Versioned – each activity is evaluated with the zones valid on its day',
  },
  mobile: {
    groupSources: 'Data sources',
    groupAuth: 'Sign-in',
    groupTraining: 'Training',
    groupGeneral: 'General',
    garmin: 'Garmin Personal API',
    strava: 'Strava export (ZIP)',
    fit: 'Upload FIT',
    intervals: 'intervals.icu',
    imported: 'Imported',
    open: 'Open',
    ready: 'Ready',
    active: 'Active',
    noLogin: 'No own login',
    oidc: 'Login service (OIDC)',
    authActive: 'active · {how}',
    authOidcHow: 'passkeys managed by the service',
    authProxyHow: 'behind a signing-in proxy',
    authLater: 'comes before deploy',
    sports: 'Sports',
    zones: 'Zones',
    zonesSub: 'v{version} · valid from {date}',
    zonesNone: 'none yet',
    language: 'Language & units',
    languageSub: 'English · metric · week starts Monday',
    theme: 'Appearance',
    ai: 'AI & privacy',
    weatherSub: 'Open-Meteo · on activities with GPS',
    weather: 'Weather',
    aiSub: 'Provider · weekly comment · what the AI sees',
    data: 'Data',
    dataSub: 'Archive {size} · CSV export · rebuild',
  },
  general: {
    language: 'Sprache · Language',
    units: 'Units',
    metric: 'Metric (km, m, kg)',
    imperial: 'Imperial (coming later)',
    weekStart: 'Week starts on',
    monday: 'Monday',
    weekHint: 'Fixed to Monday (ISO week), as streaks and weekly totals count.',
    theme: 'Appearance',
    themeSystem: 'Match system',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeHint: 'Applies to this browser.',
  },
  data: {
    confirmRebuild: 'Rebuild the database from the raw data archive? Zones, sports and your own titles/notes stay. Depending on the archive this takes a few minutes.',
    summary: 'Raw data archive {archive} ({files} files) · database {db} · {activities} activities · {days} health days',
    rebuilding: 'Rebuilding …',
    rebuild: 'Rebuild database from archive',
    export: 'Export everything as CSV',
    running: 'Rebuild running for {since} …',
    done: 'Rebuilt {ago}: {versions} versions, {files} health files.',
    failed: 'Rebuild failed: {error}',
    csvNote: 'CSV: one row per activity with time, sport, title, duration, distance, heart rate, load and source – no coordinates.',
  },
  sources: {
    garminKeyMissing: 'GARMIN_API_KEY missing',
    keyExpires: 'Key expires {date}',
    historyFrom: 'History from {date}',
    noData: 'no data yet',
    stravaOnce: 'one-off · {n} versions',
    stravaNot: 'not imported yet',
    fitUploaded: '{n} uploaded',
    fitExample: 'e.g. Wahoo rides',
    intervalsOk: 'sends workouts to the device · {n} sent',
    intervalsMissing: 'INTERVALS_API_KEY missing',
    uploadCreated: 'Activity created.',
    uploadDuplicate: 'Already there – no duplicate created.',
    uploadFailed: 'Upload failed: {error}',
    uploadBusy: 'Loading …',
    uploadButton: 'Upload FIT',
    error: 'Error:',
    metaKeyExpires: 'Key expires: {date}',
    metaActivities: 'Activities {from} – {to}',
    metaHealth: 'Health {from} – {to}',
    lastFetchFailed: 'last fetch failed {ago}',
    lastFetch: 'last fetch {ago}',
    neverFetched: 'never fetched',
    garminDesc: 'Activities including FIT file and all health values. Fetched hourly and on demand.',
    syncRunning: 'Running …',
    syncNow: 'Sync now',
    stravaDesc: 'One-off, for the time before Garmin. Duplicates: measurements stay from Garmin, title and note are taken over.',
    stravaTaken: '{n} taken over',
    stravaReplaced: '{n} replaced as duplicates',
    stravaNone: 'no export imported yet',
    stravaButton: 'Upload Strava export',
    stravaUploading: 'Uploading … {mb} MB',
    stravaRunning: 'Importing … {read} of {total} activities',
    stravaDone: 'Done: {created} new, {merged} merged, {skipped} already known, {errors} errors',
    stravaFailed: 'Import failed: {error}',
    stravaCli: 'Very large exports (several GB) are better imported from the command line: {cmd}',
    fitDesc: 'Upload a file – for rides it replaces the Garmin version of the same ride.',
    fitFiles: '{n} files',
    fitLast: 'latest: {date}',
    fitNone: 'no file uploaded yet',
    intervalsDesc: 'Planning only: workouts go to Garmin or Wahoo via intervals.icu.',
    intervalsOnDevice: '{n} sessions on the device',
    intervalsNoKey: 'Without a key everything stays a draft – set INTERVALS_API_KEY (and optionally INTERVALS_ATHLETE_ID)',
    footKeys: 'Keys live only in the server environment ({env}) – never in the repo, never in the browser. Garmin doesn’t reveal when the key expires; if you know, set it as {expires}.',
  },
  auth: {
    secret: 'Secret {state}',
    secretSet: 'set',
    secretMissing: 'missing',
    proxyHeader: 'Remote-User header only from TRUSTED_PROXIES',
    noneTitle: 'No sign-in set up yet.',
    noneText: 'Sign-in comes before deploy – until then the app only runs locally without login.',
    oidcTitle: 'Login service (OIDC)',
    oidcDesc: 'Sign in via an existing service such as Authentik, Keycloak, Pocket ID or Google.',
    proxyTitle: 'No own login',
    proxyDesc: 'For the home network or behind a proxy that already signs in (e.g. Tailscale, Cloudflare Access).',
    active: 'Active',
    off: 'Off',
    foot: 'Passkeys, two-factor and accounts are managed by the login service, not dashy-dash. Without one of the two settings the app won’t start later – it is never open by accident.',
  },
  sports: {
    loadHr: 'Heart rate',
    loadPowerOrHr: 'Power, else heart rate',
    loadEstimate: 'Heart rate, else ~ estimated',
    nameDe: 'Name (German)',
    nameEn: 'Name (English)',
    pace: 'Pace',
    noPace: 'no pace',
    loadZones: 'Zones for load',
    saving: 'Saving …',
    save: 'Save',
    cancel: 'Cancel',
    headSport: 'Sport',
    headPace: 'Pace',
    headLoad: 'Zones for load',
    headActivities: 'Activities',
    mobilePace: 'Pace ',
    mobileLoad: 'Load ',
    mobileActivities: ' activities',
    edit: 'Edit {name}',
    foot: 'The sports are fixed – every activity lands in one of them, anything unknown under “Other”. Names, pace display and zones for load can be edited with the pencil.',
  },
  zones: {
    allSports: 'all sports',
    sourceGarmin: 'Garmin',
    sourceUser: 'own',
    validFrom: 'Valid from',
    appliesTo: 'Applies to',
    restHr: 'Resting HR',
    maxHr: 'Max HR',
    lthr: 'Threshold (LTHR)',
    optional: 'optional',
    floors: 'Lower bounds in bpm',
    model: 'Zone model',
    modelLthr: '% lactate threshold (LTHR)',
    modelHrr: '% heart rate reserve (Karvonen)',
    modelMax: '% max HR',
    modelBpm: 'Own bpm',
    pcts: 'Lower bounds in %',
    preview: 'Results in bpm',
    lthrFallback: 'Without a threshold dashy-dash uses % of max HR (50 · 60 · 69 · 80 · 89%). Enter the threshold and the percentages above apply.',
    modelMeta: 'Model: {model} · {pcts}%',
    modelMetaFallback: 'Model: {model}, without a threshold by % of max HR',
    recomputing: 'Recalculating …',
    saveRecompute: 'Save & recalculate',
    createRecompute: 'Create & recalculate',
    cancel: 'Cancel',
    current: ' · current',
    validFromShort: 'valid from {date}',
    source: 'Source: {source}',
    meta: 'Resting HR {rest} · max HR {max} · threshold {lthr} · {n} activities',
    edit: 'Edit',
    delete: 'Delete',
    lastGeneral: 'The last version for all sports has to stay',
    recomputed: '~{changed} activities recalculated ({checked} checked).',
    confirmDelete: 'Delete version v{version} ({scope}, from {date})? The load will be recalculated.',
    newVersion: 'New version from …',
    empty: 'No zones yet – the first Garmin sync brings them in, or create a version.',
    foot: 'Each activity is evaluated with the zones valid on its day. The Garmin sync never overwrites your own versions; every change recalculates the load of all affected activities.',
  },
  ai: {
    statusError: 'AI status unavailable: {error}',
    loading: 'Loading …',
    provider: 'AI provider',
    claude: 'Claude (Anthropic)',
    openai: 'OpenAI (later)',
    ollama: 'Ollama, local (later)',
    off: 'Off',
    status: 'Status',
    on: 'On · {model}',
    isOff: 'AI is off',
    reasonHint: '{reason}. Chosen via the server environment: {provider} and {key}.',
    weekly: 'Weekly comment on Mondays',
    weeklyPending: 'Takes effect once an AI provider is running.',
    lastGenerated: 'Last generated {ago} for the week of {week} with {model}',
    lastError: 'Last error',
    hideStarts: 'Hide start locations in exports',
    radius: 'Radius 300 m',
    sent: 'Sent to the provider{when}:',
    sentLater: ' (once switched on)',
    never: 'Never:',
    foot: 'The AI only sees data from Garmin, Wahoo and your own Strava export. Workouts never go to the device without your click.',
  },
} satisfies Messages['settings'];
